// Small device preferences only. Never import a cloud/engine writer here.
export const MC_TRANSFER_RULES_EVENT = 'mc-transfer-rules-changed';
// The existing Storage bridge syncs every mc-* key. This namespace must remain
// outside it so a small device preference cannot become a Firestore write.
export const TRANSFER_RULES_STORAGE_PREFIX = 'matrixos-device-transfer-rules:v1:';
export const SUGGESTED_TRANSFER_RULES = Object.freeze({ targetCoverDays: 14, sourceReserve: 5, destinationMinimum: 0, transferStep: 1 });
const cleanScope = (value) => String(value || 'ALL').trim().toLowerCase();
const keyFor = (orgId, scope) => TRANSFER_RULES_STORAGE_PREFIX + encodeURIComponent(String(orgId || '').trim()) + ':' + encodeURIComponent(cleanScope(scope));
const browserStorage = () => { try { return typeof window === 'undefined' ? null : window.localStorage; } catch { return null; } };

export function validateTransferRules(value = {}) {
  const policy = {};
  const errors = [];
  for (const [key, label, positive] of [
    ['targetCoverDays', 'Days of stock', true], ['sourceReserve', 'Stock to keep', false],
    ['destinationMinimum', 'Minimum stock', false], ['transferStep', 'Send in multiples of', true],
  ]) {
    const raw = value?.[key];
    const number = typeof raw === 'number' || typeof raw === 'string' ? Number(raw) : NaN;
    const ticks = number * 1000000;
    if (raw === '' || String(raw ?? '').trim() === '' || !Number.isFinite(number)
      || (positive ? number <= 0 : number < 0) || !Number.isSafeInteger(Math.round(ticks))
      || Math.abs(ticks - Math.round(ticks)) > Number.EPSILON * Math.max(1, Math.abs(ticks)) * 2) {
      errors.push(label + ': enter ' + (positive ? 'a number greater than 0' : '0 or more') + ', with up to 6 decimal places.');
    } else policy[key] = number;
  }
  return { policy: errors.length ? null : policy, errors };
}

export function loadTransferRules({ orgId = '', scope = 'ALL', storage = browserStorage() } = {}) {
  if (!String(orgId).trim() || !storage) return { policy: null, inherited: false };
  for (const candidate of [...new Set([cleanScope(scope), 'all'])]) {
    try {
      const saved = JSON.parse(storage.getItem(keyFor(orgId, candidate)) || 'null');
      if (saved?.version !== 1) continue;
      const checked = validateTransferRules(saved.policy);
      if (checked.policy) return { policy: checked.policy, inherited: candidate !== cleanScope(scope), savedAt: saved.savedAt };
    } catch { /* A corrupt local override can still inherit valid All Sites rules. */ }
  }
  return { policy: null, inherited: false };
}

export function saveTransferRules({ orgId = '', scope = 'ALL', policy, storage = browserStorage() } = {}) {
  const checked = validateTransferRules(policy);
  if (!checked.policy) return { ok: false, errors: checked.errors };
  if (!String(orgId).trim() || !storage) return { ok: false, errors: ['Stock rules could not be saved on this device. Check your organisation and browser settings.'] };
  try {
    storage.setItem(keyFor(orgId, scope), JSON.stringify({ version: 1, policy: checked.policy, savedAt: new Date().toISOString() }));
  } catch { return { ok: false, errors: ['Stock rules could not be saved on this device. Your previous rules still apply.'] }; }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(MC_TRANSFER_RULES_EVENT));
  return { ok: true, policy: checked.policy, errors: [] };
}
